<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Set Password"
    :style="{ maxWidth: '500px' }"
    @show="handleShow"
  >
    <div class="space-y-4">
      <!-- Password Field -->
      <div class="space-y-2">
        <label for="password" class="text-sm font-medium text-gray-700"
          >Password</label
        >
        <InputText
          id="password"
          v-model="formData.password"
          type="password"
          class="w-full"
          placeholder="Enter new password"
        />
        <small v-if="v$.password.$error" class="text-red-500">
          {{ v$.password.$errors[0]?.$message }}
        </small>
      </div>

      <!-- Confirm Password Field -->
      <div class="space-y-2">
        <label for="password2" class="text-sm font-medium text-gray-700"
          >Confirm Password</label
        >
        <InputText
          id="password2"
          v-model="formData.password2"
          type="password"
          class="w-full"
          placeholder="Re-enter password"
        />
        <small v-if="v$.password2.$error" class="text-red-500">
          {{ v$.password2.$errors[0]?.$message }}
        </small>
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end gap-2 pt-2">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="isVisible = false"
        />
        <Button
          label="Set Password"
          :loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, sameAs, helpers } from "@vuelidate/validators";
import { useSetUserPassword } from "../composables/useSetUserPassword";

interface Props {
  readonly userId: number;
}

const props = defineProps<Props>();

const isVisible = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  passwordSet: [];
}>();

const { isSubmitting, setPassword } = useSetUserPassword();

const formData = reactive({
  password: "",
  password2: "",
});

const rules = computed(() => ({
  password: {
    required: helpers.withMessage("Password is required", required),
    minLength: helpers.withMessage(
      "Password must be at least 6 characters",
      minLength(6)
    ),
  },
  password2: {
    required: helpers.withMessage("Please confirm password", required),
    sameAs: helpers.withMessage(
      "Passwords do not match",
      sameAs(formData.password)
    ),
  },
}));

const v$ = useVuelidate(rules, formData);

async function handleSubmit() {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const success = await setPassword(props.userId, formData.password);
  if (success) {
    isVisible.value = false;
    emit("passwordSet");
  }
}

function handleShow() {
  // Reset form when dialog opens
  formData.password = "";
  formData.password2 = "";
  v$.value.$reset();
}
</script>
